import type {
  PerformanceClinic,
  YearlyPerformance,
} from '../api/performance.api';
import { changeRate, monthOf } from './performance';

/*
 * M-08 대시보드 지표 · 선택 월 인사이트 — 서버에 전용 필드가 없어 기존 응답으로 계산한다.
 * TODO(server): 전년 대비 · 월 평균 · 인사이트를 서버가 주면 그 값으로 바꾼다 (docs/api/README B1).
 */

export interface AnnualKpis {
  /** 연간 총 처방 금액 (= /yearly estimatedAmountPrescription) */
  prescription: number;
  /** 연간 정산 확정 금액 */
  confirmed: number;
  /** 평균 정산 비율 = 연간 확정 합 ÷ 연간 대상 합 (서버 값) */
  ratio: number | null;
  /** 월 평균 정산 금액 — 정산 완료 달의 확정 금액 평균(내림) */
  monthlyAverage: number | null;
  settledMonthCount: number;
  /** 전년 같은 기간(1월 ~ 올해 마지막 조회 달) 대비 % — 전년 자료가 없으면 null */
  prescriptionYoY: number | null;
  confirmedYoY: number | null;
}

export const getAnnualKpis = (
  yearly: YearlyPerformance,
  previous?: YearlyPerformance,
): AnnualKpis => {
  const settled = yearly.months.filter(
    (m) => m.monthlyStatusCode === 'SETTLED',
  );
  const settledSum = settled.reduce(
    (a, m) => a + m.confirmedAmountSettlement,
    0,
  );
  const lastMonth = Math.max(
    0,
    ...yearly.months.map((m) => monthOf(m.settlementYearMonth)),
  );
  const prevSame = (previous?.months ?? []).filter(
    (m) => monthOf(m.settlementYearMonth) <= lastMonth,
  );
  const prevSum = (pick: (m: (typeof prevSame)[number]) => number) =>
    prevSame.length ? prevSame.reduce((a, m) => a + pick(m), 0) : null;

  return {
    prescription: yearly.estimatedAmountPrescription,
    confirmed: yearly.confirmedAmountSettlement,
    ratio: yearly.settlementRatio,
    monthlyAverage: settled.length
      ? Math.floor(settledSum / settled.length)
      : null,
    settledMonthCount: settled.length,
    prescriptionYoY: changeRate(
      yearly.estimatedAmountPrescription,
      prevSum((m) => m.estimatedAmountPrescription),
    ),
    confirmedYoY: changeRate(
      yearly.confirmedAmountSettlement,
      prevSum((m) => m.confirmedAmountSettlement),
    ),
  };
};

/** 선택 월 표 한 줄 — 병의원 + 정산 비율 */
export interface MonthClinicRow {
  clinic: PerformanceClinic;
  /** 병의원 정산 비율 (확정 ÷ 정산 대상) */
  ratio: number | null;
}

export type InsightKey = 'top' | 'rise' | 'fall' | 'lowRatio';

export const INSIGHT_LABEL: Record<InsightKey, string> = {
  top: '최고 실적',
  rise: '가장 많이 오른 곳',
  fall: '가장 많이 줄어든 곳',
  lowRatio: '정산 비율 낮은 곳',
};

export interface MonthInsights {
  top?: { clinic: PerformanceClinic; share: number | null };
  rise?: { clinic: PerformanceClinic; rate: number; from: number; to: number };
  fall?: { clinic: PerformanceClinic; rate: number; from: number; to: number };
  lowRatio?: { clinic: PerformanceClinic; ratio: number };
}

/**
 * 인사이트 4종 (M-08 선택 월)
 * - 최고 실적: 정산 확정 금액 1위, "전체의 n%"
 * - 가장 많이 오른 · 줄어든 곳: 전월 대비 확정 금액 증감률 (두 달 모두 확정 금액이 있는 병의원)
 * - 정산 비율 낮은 곳: 병의원 정산 비율 최저
 */
export const getMonthInsights = (
  rows: MonthClinicRow[],
  previous: PerformanceClinic[] | undefined,
): MonthInsights => {
  const settled = rows.filter((r) => r.clinic.hasSettlement);
  const total = settled.reduce((a, r) => a + r.clinic.confirmedAmount, 0);
  const insights: MonthInsights = {};

  const top = [...settled].sort(
    (a, b) => b.clinic.confirmedAmount - a.clinic.confirmedAmount,
  )[0];
  if (top && top.clinic.confirmedAmount > 0) {
    insights.top = {
      clinic: top.clinic,
      share: total ? (top.clinic.confirmedAmount / total) * 100 : null,
    };
  }

  const prevById = new Map((previous ?? []).map((c) => [c.clinicId, c]));
  const changes = settled
    .map((r) => {
      const prev = prevById.get(r.clinic.clinicId);
      if (!prev?.hasSettlement || prev.confirmedAmount <= 0) return null;
      return {
        clinic: r.clinic,
        from: prev.confirmedAmount,
        to: r.clinic.confirmedAmount,
        rate: changeRate(r.clinic.confirmedAmount, prev.confirmedAmount)!,
      };
    })
    .filter((c): c is NonNullable<typeof c> => c !== null);
  const rise = [...changes].sort((a, b) => b.rate - a.rate)[0];
  if (rise && rise.rate > 0) insights.rise = rise;
  const fall = [...changes].sort((a, b) => a.rate - b.rate)[0];
  if (fall && fall.rate < 0) insights.fall = fall;

  const low = settled
    .filter((r): r is MonthClinicRow & { ratio: number } => r.ratio !== null)
    .sort((a, b) => a.ratio - b.ratio)[0];
  if (low) insights.lowRatio = { clinic: low.clinic, ratio: low.ratio };

  return insights;
};
